<template>
  <div
    class="signal-graph-container"
    @contextmenu.prevent="onContextMenu($event)"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <ConfirmationToast
      v-if="showDeleteAllConfirmationToast"
      i18n-title="removeAllEventsTitle"
      i18n-description="removeAllEventsDescription"
      :i18n-description-params="{ eventType: deleteAllEventTypeLabel }"
      :position="ScreenPosition.MiddleCenter"
      :timeoutMs="0"
      @close="onCloseDeleteAllConfirmationToast"
    />
    <svg
      ref="svg"
      class="signal-graph"
      :style="{ overflow: signalOverflowVisible ? 'visible' : 'hidden' }"
    ></svg>

    <ChartContextMenu
      ref="contextMenu"
      v-model:show="showContextMenu"
      :menuOptions="contextMenuOptions"
      :signalName="title"
      :actualDataMin="yDataActualRange?.min ?? null"
      :actualDataMax="yDataActualRange?.max ?? null"
      :pane="pane"
      @invert-polarity="invertSignalPolarity"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType, toRaw } from 'vue';
import { type EventColorEntry, type GraphPoint } from '@/common/ui';
import { SignalEvent } from '@/modules/epoch/data-access';

import { SignalGraphD3 } from './signal-graph.d3';

import { PositionSignalGraphD3 } from './position-signal-graph.d3';
import { Spo2SignalGraphD3 } from './spo2-signal-graph.d3';
import { HeartRateSignalGraphD3 } from './heart-rate-signal-graph.d3';

import { EpochGraphTypes } from '@/modules/epoch/types';
import {
  EventType,
  NotificationTypes,
  type SafeAny,
  ScreenPosition,
  type SignalPanel
} from '@/common/types';
import { isEqual, throttle } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import {
  getOverlappingEventsWithIDs,
  getTranslatedValueWithArtifact,
  isOverlappingEvent,
  overlappingRespiratoryEventIDs
} from '@/core/helpers';

import { ChartContextMenu } from '../chart-context-menu';
import '@imengyu/vue3-context-menu/lib/vue3-context-menu.css';
import { checkValidationWithNotification } from '@/common/utils';
import { keyboardShortcutMapping, keyboardZoomMapping } from './keyboard-shortcut-mapping';

import { signalAmplitudeRules } from './signalAmplitudeRules';
import { getSignalFromEpochEntry } from '../../../data-access/actions/signalHelpers';
import { ConfirmationToast } from '@/common/ui';

const MAX_OVERLAPPING_EVENTS = 21;

export default defineComponent({
  name: 'SignalGraph',
  components: {
    ConfirmationToast,
    ChartContextMenu
  },
  props: {
    color: {
      type: String,
      required: true
    },
    lineColor: {
      type: String,
      required: true
    },
    eventColors: {
      type: Array as PropType<EventColorEntry[]>,
      required: false,
      default: () => []
    },
    shortcutKeys: {
      type: Object as PropType<Record<string, string>>,
      required: false,
      default: () => ({})
    },
    duration: {
      type: Number,
      required: true
    },
    epoch: {
      type: [String, Array, Number] as PropType<number | string | string[]>,
      required: true
    },
    errorTimestamp: {
      type: Number,
      required: false
    },
    isReadOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    eventTypes: {
      type: Array as PropType<string[]>,
      required: false
    },
    events: {
      type: Array as PropType<SignalEvent[]>,
      required: false
    },
    graphType: {
      type: Number,
      required: true
    },
    height: {
      type: String,
      required: true
    },
    startDatetime: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: true
    },
    yData: {
      type: Array as PropType<number[]>,
      required: true
    },
    yDataMapped: {
      type: Array as PropType<number[]>,
      required: false,
      default: () => []
    },
    yDataAdjacent: {
      type: Array as PropType<number[]>,
      required: false,
      default: null
    },
    epochAdjacent: {
      type: Number,
      required: false,
      default: null
    },
    signalName: {
      type: String,
      required: true
    },
    lastUpdatedEpochModifier: {
      type: String,
      required: true
    },
    isFullNight: {
      type: Boolean,
      required: true,
      default: false
    },
    pane: {
      type: String as PropType<SignalPanel | undefined>,
      required: false,
      default: undefined
    }
  },
  data() {
    return {
      ScreenPosition,
      isEventDetailModalOpen: false,
      d3Client: null as unknown as SignalGraphD3,
      throttledPaintChart: null as unknown as () => void,
      showContextMenu: false,
      showDeleteAllConfirmationToast: false,
      deleteAllEventType: '',
      contextMenuOptions: {
        x: 0,
        y: 0,
        minWidth: 200
      },
      isHovered: false
    };
  },
  computed: {
    ...mapGetters({
      getSignalMapping: 'epoch/getSignalMapping',
      getFullNightSignalData: 'epoch/getFullNightSignalData',
      scoreSet: 'scoreSet/getScoreSet'
    }),
    deleteAllEventTypeLabel(): string {
      return this.deleteAllEventType ? getTranslatedValueWithArtifact(this.deleteAllEventType) : '';
    },
    eventColorsMap(): Record<string, string> {
      return Object.fromEntries(this.eventColors.map((ec) => [ec.eventType, ec.color]));
    },
    artifactColor(): string {
      return this.$store.state.epoch.settings.artifactColor ?? '#FF9999';
    },
    yDataActualRange(): { min: number; max: number } | null {
      // Hot path: this computed re-runs on every epoch transition × ~16 charts.
      // The previous implementation used `.flat().filter().Math.min(...).Math.max(...)`
      // which allocated two intermediate arrays of ~15-30K items, then spread
      // up to ~30K floats as function arguments — both memory-churn and
      // call-stack expensive. A single-pass loop over toRaw'd data eliminates
      // all of that and skips reactive Proxy traps in the inner loop.
      const yData = toRaw(this.yData) as (number | number[])[] | undefined | null;
      if (!yData?.length) return null;

      let min = Infinity;
      let max = -Infinity;
      let hasValue = false;

      for (let i = 0; i < yData.length; i += 1) {
        const item = yData[i];
        if (Array.isArray(item)) {
          for (let j = 0; j < item.length; j += 1) {
            const v = item[j];
            if (isFinite(v)) {
              if (v < min) min = v;
              if (v > max) max = v;
              hasValue = true;
            }
          }
        } else if (isFinite(item as number)) {
          const v = item as number;
          if (v < min) min = v;
          if (v > max) max = v;
          hasValue = true;
        }
      }

      return hasValue ? { min, max } : null;
    },
    signalMapping() {
      if (this.isFullNight) {
        return this.getFullNightSignalData(this.signalName)?.mapping;
      }

      return this.getSignalMapping(this.signalName);
    },
    epochSize() {
      return this.$route.params.epochSize;
    },
    isChartEditable() {
      return (!this.isFullNight && this.eventTypes?.length) as boolean;
    },
    overlappedRespiratoryEvents() {
      return this.$store.state.epoch.overlappedRespiratoryEvents;
    },
    eventList() {
      return this.$store.state.epoch.signalEvents;
    },
    ecgPolarityInversionComputed() {
      return this.scoreSet?.ecgPolarityInversion;
    },
    nasalPressurePolarityInversionComputed(): boolean {
      return this.$store.state.epoch.graph.polarityInversion?.[this.signalName] ?? false;
    },
    signalOverflowVisible(): boolean {
      return this.$store.state.epoch.graph.overflowVisible?.[this.signalName] ?? false;
    }
  },
  created() {
    this.throttledPaintChart = throttle(this.paintChart, 50);
  },
  mounted() {
    this.initializeD3Client();
    this.throttledPaintChart();

    window.addEventListener('resize', this.throttledPaintChart);
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('signal-zoom-in', this.onZoomIn);
    window.addEventListener('signal-zoom-out', this.onZoomOut);
    window.addEventListener('fit-all-to-pane', this.onFitAllToPane);

    this.$nextTick(() => {
      if (this.lastUpdatedEpochModifier === 'epoch') {
        this.setSettingsSignalZoom();
      } else {
        this.setSourceSignalZoom();
      }
    });
  },
  beforeUnmount() {
    if (this.d3Client) {
      // Drop the SpO2 lookup closure first — it captures `this` (and through
      // it the Vuex store), so leaving it pinned on a possibly-still-
      // referenced d3Client instance is a pointless retention.
      if (this.d3Client instanceof Spo2SignalGraphD3) {
        this.d3Client.setSpo2DataLookup(null);
      }
      this.d3Client.cleanUp();
    }
    window.removeEventListener('resize', this.throttledPaintChart);
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('signal-zoom-in', this.onZoomIn);
    window.removeEventListener('signal-zoom-out', this.onZoomOut);
    window.removeEventListener('fit-all-to-pane', this.onFitAllToPane);
  },
  methods: {
    ...mapActions({
      updateScoreSet: 'scoreSet/updateScoreSet'
    }),
    onFitAllToPane(e: Event) {
      const evt = e as CustomEvent<SignalPanel | undefined>;
      if (this.pane && evt.detail !== this.pane) return;

      (this.$refs.contextMenu as any).fitToScreenZoom?.();
    },
    onZoomIn(e: Event) {
      const evt = e as CustomEvent<string>;
      if (evt.detail !== this.title) return;

      const zoomMap = keyboardZoomMapping(this.$refs.contextMenu);
      zoomMap['+']?.();
    },
    onZoomOut(e: Event) {
      const evt = e as CustomEvent<string>;
      if (evt.detail !== this.title) return;

      const zoomMap = keyboardZoomMapping(this.$refs.contextMenu);
      zoomMap['-']?.();
    },
    handleKeyDown(e: KeyboardEvent) {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      if (this.isChartEditable && this.isEventDetailModalOpen) {
        const map = keyboardShortcutMapping(this.d3Client, this.signalName, this.shortcutKeys);

        if (map[key]) {
          e.preventDefault();
          e.stopImmediatePropagation();
          Promise.resolve(map[key]());
        }
        return;
      }

      if (document.querySelector('.event-details[signal-name]')) return;

      if (this.isHovered || this.showContextMenu) {
        const zoomMap = keyboardZoomMapping(this.$refs.contextMenu);

        if (zoomMap[key]) {
          e.preventDefault();
          zoomMap[key]();
        }
      }
    },
    handleClickOutside() {
      this.d3Client.clearBrush();
    },
    initializeD3Client() {
      // markRaw: the d3 client is a complex class instance with dozens of
      // internal fields (xScale, yScale, graphGroup, this.data, this.events,
      // rawYData, etc.). Without markRaw, declaring `d3Client` in `data()`
      // causes Vue 3 to recursively wrap the entire instance in a reactive
      // Proxy, so every field read/write inside the d3 layer's hot paint
      // path goes through proxyMap.get/set. With ~16 charts × dozens of
      // fields × every paint, this was the dominant residual reactivity
      // overhead after the coord-cache eliminated allocations. We never
      // observe d3Client's internal state from templates or computeds —
      // only call methods on it — so reactivity on its fields is wasted.
      switch (this.title) {
        case 'Position':
          this.d3Client = markRaw(new PositionSignalGraphD3(this.$refs.svg as SVGElement));
          break;
        case 'SpO2':
          this.d3Client = markRaw(new Spo2SignalGraphD3(this.$refs.svg as SVGElement));
          break;
        case 'Heart_rate':
          this.d3Client = markRaw(new HeartRateSignalGraphD3(this.$refs.svg as SVGElement));
          break;
        default:
          this.d3Client = markRaw(new SignalGraphD3(this.$refs.svg as SVGElement));
      }
      if (this.isChartEditable) {
        this.d3Client.handleRestrictedEventUpdate = this.handleRestrictedEventUpdate.bind(this);
        this.d3Client
          .enableEvents()
          .subscribeForInsertEvent(this.insertEvent.bind(this))
          .subscribeForUpdateEvent(this.updateEvent.bind(this))
          .subscribeForDeleteEvent(this.deleteEvent.bind(this))
          .subscribeForEventDetailStatus(this.setEventDetailStatus.bind(this))
          .subscribeForDeleteAllEvents(this.deleteAllEvents.bind(this));
      } else {
        this.d3Client.disableEvents();
      }
      if (this.isReadOnly) {
        this.d3Client.setReadOnly(this.isReadOnly);
      }
      this.d3Client.setYAxisPanCallback?.(({ min, max }) => {
        this.$store.commit('epoch/setSignalZoom', {
          signal: this.signalName,
          to: [min, max]
        });
      });
      this.d3Client.buildSvgElements().attachWindowResizeEvent();
    },
    handleRestrictedEventUpdate() {
      this.$store.commit(
        'addNotification',
        {
          i18nTitle: 'warningEventUpdateBlocked',
          i18nDescription: this.isReadOnly
            ? 'warningViewOnlyDescription'
            : 'warningEventUpdateBlockedDescription',
          type: NotificationTypes.Warning
        },
        { root: true }
      );
    },
    setEventDetailStatus(isOpen: boolean) {
      this.isEventDetailModalOpen = isOpen;
    },
    paintChart() {
      if (!this.d3Client) return;

      const { measurementStart, measurementEnd } = this.scoreSet ?? {};
      const isECG = this.title.toLowerCase() === 'ecg';
      const isPosition = this.title.toLowerCase() === 'position';
      const isNasalPressure = this.title.toLowerCase() === 'nasal_pressure';
      const ecgInversionFlag = this.ecgPolarityInversionComputed ?? false;
      const nasalPressureInversionFlag = this.nasalPressurePolarityInversionComputed ?? false;

      const shouldInvert = isECG
        ? ecgInversionFlag
        : isNasalPressure
          ? nasalPressureInversionFlag
          : false;
      const shouldShowMarker = isPosition ? ecgInversionFlag : false;

      // Unwrap Vue's reactive proxy — signal arrays are markRaw'd at the data layer,
      // but the prop/computed plumbing wraps the outer reference.  buildFastPath
      // iterates 15K+ entries per signal × ~16 signals; each proxy get() was adding
      // ~600ms of reactivity overhead at 2min windows in profiling.
      const yData = toRaw(this.yData) as any[];
      const yDataAdj = this.yDataAdjacent ? (toRaw(this.yDataAdjacent) as any[]) : null;
      // Same treatment for the secondary props/computeds read on every paint.
      // Each is iterated/read downstream (filter/map/passed to updateGraph) and
      // accessing them through the reactive Proxy adds avoidable get() traps to
      // the hot path that runs 16× per granular tick.
      const yDataMapped = this.yDataMapped ? (toRaw(this.yDataMapped) as any[]) : this.yDataMapped;
      const events = this.events ? (toRaw(this.events) as any[]) : this.events;
      const eventList = toRaw(this.eventList) as Record<string, any[]> | undefined;
      const signalMapping = this.signalMapping ? toRaw(this.signalMapping) : this.signalMapping;
      const dur = this.duration as number;

      // Fast path applies to scalar (non-multiY, non-full-night) signals.
      const isMultiY = Array.isArray(yData?.[0]);
      const canUseFastPath =
        Array.isArray(yData) && yData.length > 1 && !this.isFullNight && !isMultiY;

      const effectiveEpoch = Number(this.epoch ?? 0);

      const baseEpoch =
        this.epochAdjacent != null
          ? this.epochAdjacent < effectiveEpoch
            ? this.epochAdjacent + 1
            : this.epochAdjacent - 1
          : Math.floor(effectiveEpoch);

      // Extend the fast-path raw buffer to also cover the adjacent epoch so the
      // visible window can slide into it during granular navigation. Without
      // this, scalar signals (which take the fast path) render blank in the
      // newly-revealed portion — only multi-Y signals (Heart_rate, SpO2,
      // Position) work, because they take the slow path which already merges
      // currentCoords + adjacentCoords into `combinedData`.
      // Anchor at the EARLIER of (current, adjacent) so sample 0 sits at the
      // start of that epoch. The d3 layer's xScale.domain slides with the
      // fractional epoch and clips the off-screen samples.
      const hasAdjacentForFast = canUseFastPath && yDataAdj?.length && this.epochAdjacent != null;
      const fullFastYData = hasAdjacentForFast
        ? (this.epochAdjacent as number) < baseEpoch
          ? (yDataAdj as any[]).concat(yData)
          : yData.concat(yDataAdj as any[])
        : yData;
      const fastAnchorEpoch =
        hasAdjacentForFast && (this.epochAdjacent as number) < baseEpoch
          ? (this.epochAdjacent as number)
          : baseEpoch;

      // xTick is per single-epoch span (dur / (N-1)).
      const rawXTick = canUseFastPath ? dur / (yData.length - 1) : undefined;

      // When fullFastYData spans two epochs, buildFastPath iterates ALL samples
      // and emits SVG path commands beyond the plot area (px < 0 on the left,
      // px > plotW on the right). There is no clip-path on the line element, so
      // those off-domain segments leak past the y-axis (left) and the last
      // x-axis tick (right) during granular navigation. Mirror what the slow
      // path does at line 424 (filter to [windowStart, windowEnd]) by slicing
      // the raw buffer to the visible window with a 1-sample margin on each
      // side so line endpoints stay continuous at the edges.
      let fastYData = fullFastYData;
      let fastRawXBase = canUseFastPath
        ? new Date(this.startDatetime as string).getTime() / 1000 + fastAnchorEpoch * dur
        : undefined;
      if (hasAdjacentForFast && canUseFastPath) {
        const samplesPerEpoch = yData.length - 1;
        const offsetSamples = (effectiveEpoch - fastAnchorEpoch) * samplesPerEpoch;
        const iStart = Math.max(0, Math.floor(offsetSamples) - 1);
        const iEnd = Math.min(fullFastYData.length, Math.ceil(offsetSamples) + samplesPerEpoch + 2);
        fastYData = fullFastYData.slice(iStart, iEnd);
        fastRawXBase = (fastRawXBase as number) + iStart * (rawXTick as number);
      }
      const rawXBase = fastRawXBase;

      const artifactEvents = this.isFullNight
        ? (eventList?.[this.signalName]?.filter((e: any) => e.eventType.includes('Artifact')) ?? [])
        : [];

      const currentCoords = SignalGraphD3.breakdownDataIntoCoords(
        this.startDatetime,
        this.duration,
        yData,
        baseEpoch,
        this.isFullNight,
        measurementStart,
        measurementEnd
      );

      let combinedData = currentCoords;
      if (yDataAdj?.length && this.epochAdjacent != null) {
        const adjacentCoords = SignalGraphD3.breakdownDataIntoCoords(
          this.startDatetime,
          this.duration,
          yDataAdj,
          this.epochAdjacent,
          this.isFullNight
        );
        const merged =
          this.epochAdjacent < baseEpoch
            ? [...adjacentCoords, ...currentCoords]
            : [...currentCoords, ...adjacentCoords];

        const startTimeSec = new Date(this.startDatetime as string).getTime() / 1000;
        const windowStart = startTimeSec + effectiveEpoch * (this.duration as number);
        const windowEnd = windowStart + (this.duration as number);
        combinedData = merged.filter((p) => p.x >= windowStart && p.x <= windowEnd);
      }

      if (this.title === 'SpO2') {
        // Wire a lookup the d3 layer can call at hover time to fetch SpO2
        // sample coords across the *exact* time range of the hovered desat
        // event. This is strictly more correct than the prior fixed
        // `extendedData = [baseEpoch-1, base, baseEpoch+1]` snapshot:
        //
        //  - It walks only the epochs the event actually spans, no matter
        //    how long the event is or how far past the visible window it
        //    extends. Long desats whose lowest SpO2 sample (i.e. the value
        //    that defines the drop) sits outside the visible window — the
        //    case the user observed during gradual navigation — now get a
        //    correct drop value.
        //  - It sidesteps an overshoot-tick mismatch that the static
        //    snapshot had: `Math.floor(effectiveEpoch)` and paintChart's
        //    `baseEpoch` (the one that respects `epochAdjacent` for the
        //    actually-rendered window) diverge for the brief moment between
        //    a wheel-cross's offset commit (`offset = size`) and the
        //    sync-flush watcher resetting it post-route-change. The lookup
        //    is keyed by event time, not epoch number, so it never drifts.
        //
        // `breakdownDataIntoCoords` is WeakMap-memoized on the underlying
        // signal array, so repeat hover lookups over the same epochs are
        // zero-allocation cache hits. For full-night view we still fall back
        // to plain `currentCoords` since the hypnogram-mode SpO2 panel
        // already has the entire study in one buffer.
        const spo2Client = this.d3Client as Spo2SignalGraphD3;

        if (this.isFullNight) {
          spo2Client.setSpo2DataLookup(null);
          spo2Client.setExtendedData(currentCoords);
        } else {
          const lookup = (rangeStartSec: number, rangeEndSec: number) => {
            if (!Number.isFinite(rangeStartSec) || !Number.isFinite(rangeEndSec)) return [];
            const studyStartSec = this.startDatetime
              ? new Date(this.startDatetime).getTime() / 1000
              : 0;
            const dur = this.duration as number;
            if (!dur || dur <= 0) return [];

            // Inclusive epoch bounds covering [rangeStartSec, rangeEndSec].
            const startEpoch = Math.floor((rangeStartSec - studyStartSec) / dur);
            const endEpoch = Math.floor((rangeEndSec - studyStartSec) / dur);

            // Defensive caps so a runaway range (corrupt event data) can't
            // iterate the whole study. A 2 min window × 32 epochs = 64 min
            // of coverage, far beyond any realistic desat or apnea event.
            const MAX_EPOCH_SPAN = 32;
            const lo = Math.max(0, startEpoch);
            const hi = Math.min(endEpoch, lo + MAX_EPOCH_SPAN);

            const result: GraphPoint<SafeAny>[] = [];
            for (let epochNum = lo; epochNum <= hi; epochNum += 1) {
              const entry = (this.$store.state.epoch?.epochData as SafeAny)?.sets?.[dur]?.[
                epochNum
              ];
              const signal = getSignalFromEpochEntry(
                entry,
                this.$store.state.epoch,
                this.$store.state,
                dur
              )?.[this.signalName] as number[] | undefined;
              if (!signal?.length) continue;
              const coords = SignalGraphD3.breakdownDataIntoCoords(
                this.startDatetime,
                dur,
                signal,
                epochNum
              );
              for (const p of coords) {
                if (p.x >= rangeStartSec && p.x <= rangeEndSec) result.push(p);
              }
            }
            return result;
          };

          spo2Client.setSpo2DataLookup(lookup);
          // Keep extendedData populated as a defensive fallback, but the
          // lookup is the source of truth at hover time. Centered on
          // paintChart's `baseEpoch` (not Math.floor(effectiveEpoch)) so the
          // fallback is also correct during overshoot ticks.
          const coordsForStoredEpoch = (epochNum: number) => {
            const entry = (this.$store.state.epoch?.epochData as SafeAny)?.sets?.[dur]?.[epochNum];
            const signal = getSignalFromEpochEntry(
              entry,
              this.$store.state.epoch,
              this.$store.state,
              dur
            )?.[this.signalName] as number[] | undefined;
            return signal?.length
              ? SignalGraphD3.breakdownDataIntoCoords(this.startDatetime, dur, signal, epochNum)
              : [];
          };
          spo2Client.setExtendedData([
            ...coordsForStoredEpoch(baseEpoch - 1),
            ...currentCoords,
            ...coordsForStoredEpoch(baseEpoch + 1)
          ]);
        }
      }

      this.d3Client.updateGraph({
        color: this.color,
        artifactEvents,
        data: combinedData,
        mappedData: SignalGraphD3.breakdownDataIntoCoords(
          this.startDatetime,
          this.duration,
          yDataMapped,
          baseEpoch
        ),
        duration: this.duration as number,
        epoch: effectiveEpoch,
        events: events as SignalEvent[] | undefined,
        graphType: this.graphType as EpochGraphTypes,
        mapping: signalMapping,
        startDatetime: this.startDatetime as string,
        title: this.title,
        acceptedEventTypes: this.eventTypes as string[] | undefined,
        lineColor: this.lineColor,
        eventColors: this.eventColorsMap,
        shortcutKeys: this.shortcutKeys,
        artifactColor: this.artifactColor,
        polarityInversion: shouldInvert || shouldShowMarker,
        rawYData: canUseFastPath ? fastYData : undefined,
        rawXBase,
        rawXTick
      });
    },

    async insertEvent({
      id,
      start,
      duration,
      eventIndex
    }: {
      id: string;
      start: string;
      duration: number;
      eventIndex?: number;
    }) {
      const map = this.$store.state.epoch?.eventIdMap;
      const resolvedCurrentId = map?.[id] ?? id;
      const isOverlapping = isOverlappingEvent(
        { start, duration, id: resolvedCurrentId },
        this.events,
        map
      );
      const eventType = this.eventTypes?.[eventIndex || 0] || '';
      const overlappingEvents = isOverlapping
        ? getOverlappingEventsWithIDs(
            { start, duration, id: resolvedCurrentId },
            this.events,
            map
          ) || []
        : [];

      if (isOverlapping && overlappingEvents.length > 0) {
        const isValid = checkValidationWithNotification(
          eventType,
          start,
          duration,
          this.$store.state.scoreSet.scoreSet.data,
          this.$store.state.epoch.signalEvents,
          this.$store
        );

        if (!isValid) {
          this.throttledPaintChart();
          return;
        }

        if (overlappingEvents.length >= MAX_OVERLAPPING_EVENTS) {
          this.$store.commit(
            'addNotification',
            {
              i18nTitle: 'errorAddScoreSetEvents',
              i18nDescription: 'errorOverlapLimitDescription',
              type: NotificationTypes.Warning
            },
            { root: true }
          );
          return;
        }

        const baseEvent = overlappingEvents[0];
        const baseEventId = map?.[baseEvent.id] ?? baseEvent.id;

        const redundantEvents = overlappingEvents.filter((e) => {
          const rId = map?.[e.id] ?? e.id;
          return rId !== baseEventId;
        });

        for (const redundant of redundantEvents) {
          const redundantId = map?.[redundant.id] ?? redundant.id;
          await this.deleteEvent({ id: redundantId });
        }

        const isUpdated = await this.$store.dispatch('epoch/updateStudyScoreSetEvent', {
          start,
          duration,
          eventId: baseEventId,
          type: eventType,
          studyId: this.$route.params.id,
          epochSize: this.epochSize
        });

        if (isUpdated) this.$emit('update:signal-graph');
        return;
      }

      const isStudyScoreEventAdded = await this.$store.dispatch('epoch/insertStudyScoreSetEvent', {
        id: resolvedCurrentId,
        start,
        duration,
        type: eventType,
        studyId: this.$route.params.id,
        epochSize: this.epochSize
      });

      if (isStudyScoreEventAdded === false) return;

      this.$emit('update:signal-graph');
    },
    async updateEvent(event: SignalEvent): Promise<void> {
      const { start, duration, id, eventIndex, eventType: currentEventType } = event;
      const map = this.$store.state.epoch?.eventIdMap;
      const resolvedId = map?.[id] ?? id;
      const isOverlapping = isOverlappingEvent(
        { start, duration, id: resolvedId },
        this.events,
        map
      );
      const eventType = this.eventTypes?.[eventIndex ?? -1] ?? currentEventType;
      let hasOverlappingEvents = false;

      if (eventType === EventType.WAKE) {
        hasOverlappingEvents =
          overlappingRespiratoryEventIDs(resolvedId, start, duration, this.eventList).length > 0;

        if (hasOverlappingEvents) {
          this.$store.commit(
            'addNotification',
            {
              i18nTitle: 'hasRespiratoryEventTitle',
              i18nDescription: 'hasRespiratoryEventWarning',
              type: NotificationTypes.Warning
            },
            { root: true }
          );
        }
      }

      if (isOverlapping) {
        const isValid = checkValidationWithNotification(
          eventType || '',
          start,
          duration,
          this.$store.state.scoreSet.scoreSet.data,
          this.$store.state.epoch.signalEvents,
          this.$store
        );

        if (!isValid) {
          this.throttledPaintChart();
          return;
        }

        const redundantEvents =
          getOverlappingEventsWithIDs({ start, duration, id: resolvedId }, this.events, map) || [];

        if (redundantEvents.length >= MAX_OVERLAPPING_EVENTS) {
          this.$store.commit(
            'addNotification',
            {
              i18nTitle: 'errorAddScoreSetEvents',
              i18nDescription: 'errorOverlapLimitDescription',
              type: NotificationTypes.Warning
            },
            { root: true }
          );
          return;
        }

        for (const redundant of redundantEvents) {
          const currentMap = this.$store.state.epoch?.eventIdMap;
          const redundantId = currentMap?.[redundant.id] ?? redundant.id;

          if (redundantId !== resolvedId) {
            await this.deleteEvent({ id: redundantId });
          }
        }
      }

      const finalId = this.$store.state.epoch?.eventIdMap?.[id] ?? resolvedId;

      const isUpdated = await this.$store.dispatch('epoch/updateStudyScoreSetEvent', {
        start,
        duration,
        eventId: finalId,
        type: eventType,
        studyId: this.$route.params.id,
        epochSize: this.epochSize
      });

      if (isUpdated !== false) {
        this.$emit('update:signal-graph');
      }
    },
    async deleteEvent({ id }: { id: string }): Promise<void> {
      const map = this.$store.state.epoch?.eventIdMap;
      const finalIdToDelete = map?.[id] ?? id;

      await this.$store.dispatch('epoch/deleteStudyScoreSetEvent', {
        eventId: finalIdToDelete,
        studyId: this.$route.params.id,
        epochSize: this.epochSize
      });
    },
    async onCloseDeleteAllConfirmationToast(confirmed: boolean): Promise<void> {
      this.showDeleteAllConfirmationToast = false;

      const { deleteAllEventType } = this;
      this.deleteAllEventType = '';

      if (!confirmed || !deleteAllEventType) return;

      await this.$store.dispatch('epoch/deleteStudyScoreSetAllEventsOfType', {
        studyId: this.$route.params.id,
        eventType: deleteAllEventType,
        epochSize: this.epochSize
      });
    },
    deleteAllEvents({ eventType }: { eventType: string }): void {
      this.deleteAllEventType = eventType;
      this.showDeleteAllConfirmationToast = true;
    },
    async deleteOverlappingEvents({ start, duration, id }) {
      const map = this.$store.state.epoch?.eventIdMap;
      const resolvedTargetId = map?.[id] ?? id;

      const overlappingEvents =
        getOverlappingEventsWithIDs({ start, duration, id: resolvedTargetId }, this.events, map) ||
        [];

      for (const event of overlappingEvents) {
        const resolvedEventId = map?.[event.id] ?? event.id;

        if (resolvedTargetId !== resolvedEventId) {
          await this.deleteEvent({ id: event.id });
        }
      }
    },
    onContextMenu(event: MouseEvent) {
      event.preventDefault();
      this.contextMenuOptions.x = event.clientX;
      this.contextMenuOptions.y = event.clientY;
      this.showContextMenu = true;
    },
    setSettingsSignalZoom() {
      const epochState = this.$store.state.epoch;
      const savedZoom = epochState.graph.zoom?.[this.signalName];

      // Keep existing zoom if present
      if (savedZoom) {
        this.$store.commit('epoch/setSignalZoom', {
          signal: this.signalName,
          to: [savedZoom.min, savedZoom.max]
        });
        return;
      }

      // Otherwise use current mapping
      if (!this.signalMapping) return;
      const { min, max } = this.signalMapping;

      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: [min, max]
      });
    },
    setSourceSignalZoom() {
      const epochState = this.$store.state.epoch;
      // If user already zoomed - keep it
      const existingZoom = epochState.graph.zoom?.[this.signalName];
      if (existingZoom) {
        this.$store.commit('epoch/setSignalZoom', {
          signal: this.signalName,
          to: [existingZoom.min, existingZoom.max]
        });
        return;
      }
      // No user zoom yet - initialize from amplitude rules
      const rule = signalAmplitudeRules[this.signalName];
      if (rule && rule.min !== 'auto' && rule.max !== 'auto') {
        this.$store.commit('epoch/setSignalZoom', {
          signal: this.signalName,
          to: [Number(rule.min), Number(rule.max)]
        });
        return;
      }
      // Auto-signals - fallback to backend mapping
      const windowSize = this.epochSize?.toString() ?? '';
      const stringEpoch = this.epoch?.toString() ?? '';
      const sourceMapping =
        epochState?.epochData?.sets?.[windowSize]?.[stringEpoch]?.mapping?.[this.signalName];
      if (!sourceMapping) return;
      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: [sourceMapping.min, sourceMapping.max]
      });
    },
    invertSignalPolarity(signalName: string) {
      const name = signalName.toLowerCase();

      if (name === 'ecg') {
        const newInversionState = !(this.ecgPolarityInversionComputed ?? false);

        this.$store.commit('epoch/updateSignalInversion', {
          signal: signalName,
          ecgPolarityInversion: newInversionState
        });

        this.updateScoreSet({
          sleepStudyID: this.$route.params.id,
          payload: { ecgPolarityInversion: newInversionState },
          epochSize: this.epochSize
        });
        return;
      }

      if (name === 'nasal_pressure') {
        this.$store.commit('epoch/setSignalPolarityInversion', {
          signal: signalName,
          value: !(this.nasalPressurePolarityInversionComputed ?? false)
        });
      }
    }
  },
  watch: {
    signalMapping: {
      handler(newMapping, oldMapping) {
        if (!isEqual(newMapping, oldMapping)) {
          this.throttledPaintChart();
        }
      },
      deep: true
    },
    isFullNight(value) {
      this.d3Client.setEditable(!value);
    },
    overlappedRespiratoryEvents(value) {
      if (this.d3Client) {
        this.d3Client.setRespiratoryCollisionEvents(value);
      }
    },
    epochSize: {
      handler(newSize, oldSize) {
        if (newSize !== oldSize) {
          if (newSize !== '-1') {
            this.paintChart();
          }
          this.setSourceSignalZoom();
        }
      }
    },
    readOnly() {
      this.d3Client.setReadOnly(this.isReadOnly);
    },
    color() {
      this.throttledPaintChart();
    },
    lineColor() {
      this.throttledPaintChart();
    },
    eventColors: {
      handler() {
        this.throttledPaintChart();
      },
      deep: true
    },
    shortcutKeys: {
      handler() {
        this.throttledPaintChart();
      },
      deep: true
    },
    artifactColor() {
      this.throttledPaintChart();
    },
    errorTimestamp() {
      this.throttledPaintChart();
    },
    startDatetime() {
      this.throttledPaintChart();
    },
    yData() {
      this.throttledPaintChart();
    },
    events: {
      handler() {
        this.throttledPaintChart();
      },
      deep: true
    },
    height() {
      this.throttledPaintChart();
    },
    ecgPolarityInversionComputed(newVal, oldVal) {
      if (newVal !== oldVal) this.throttledPaintChart();
    },
    nasalPressurePolarityInversionComputed(newVal, oldVal) {
      if (newVal !== oldVal) this.throttledPaintChart();
    },
    epoch(newVal, oldVal) {
      if (newVal !== oldVal) this.throttledPaintChart();
    },
    yDataAdjacent() {
      this.throttledPaintChart();
    }
  }
});
</script>

<!-- not scoped for now because of some events styling not applying -->
<style lang="scss" src="./signal-graph.style.scss"></style>
